import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardList,
  Phone,
  Search,
  Banknote,
  Landmark,
  CheckCircle2,
  Undo2,
  ChevronDown
} from 'lucide-react';
import api from '../config/api';
import { showNotification } from '../components/GlobalNotification';
import { useLanguage } from '../context/LanguageContext';
import { orderLabel } from '../utils/itemName';

/**
 * Bills handed over on credit. A due is taken at the till against a name and a
 * mobile number, and stays outstanding here until the money comes in.
 *
 * Settling does not rewrite the sale - it stays a credit sale on the day it was
 * made, which is why the figures for an old day never move. What settling does
 * is put the money in whichever drawer collected it.
 */
function Dues() {
  const { t, language } = useLanguage();
  const [view, setView] = useState('pending');
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [settling, setSettling] = useState(null);
  // What is being paid off right now, per bill. Blank means the whole balance.
  const [amounts, setAmounts] = useState({});
  // One figure per customer. A regular hands over a single sum for their whole
  // slate, not a breakdown per bill - the server cascades it oldest-bill-first.
  const [customerAmounts, setCustomerAmounts] = useState({});
  // Which customers are opened up. Collapsed is the default: the page is read
  // far more often than it is acted on.
  const [openCustomers, setOpenCustomers] = useState(() => new Set());
  // In-app stand-in for window.confirm. The native dialog sits mid-screen,
  // which on a phone is exactly where the keyboard is after typing an amount -
  // this one is pinned near the top so it always stays visible.
  const [confirmState, setConfirmState] = useState(null);

  const askConfirm = (message) =>
    new Promise((resolve) => {
      // Drop the keyboard first, so the dialog is never behind it
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
      setConfirmState({ message, resolve });
    });

  const closeConfirm = (answer) => {
    if (confirmState) confirmState.resolve(answer);
    setConfirmState(null);
  };

  const loadDues = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/orders/dues', { params: { status: view } });

      // Normalised on the way in. A browser holding a fresh bundle can easily
      // be talking to an API that has not been redeployed yet, and a bill
      // arriving without balance/paid/payments used to throw inside the card -
      // which took the whole list down and left nothing to press.
      setBills(
        (response.data || []).map((bill) => ({
          ...bill,
          amount: bill.amount ?? 0,
          paid: bill.paid ?? 0,
          balance: bill.balance ?? bill.amount ?? 0,
          items: bill.items ?? [],
          payments: bill.payments ?? []
        }))
      );
    } catch (error) {
      showNotification('error', t('Could not load the dues.'));
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [view, t]);

  useEffect(() => {
    loadDues();
  }, [loadDues]);

  const toggleCustomer = (key) => {
    setOpenCustomers((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Name or number - a cashier chasing a due has one or the other to hand
  const visibleBills = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return bills;

    return bills.filter(
      (bill) =>
        bill.customerName.toLowerCase().includes(term) ||
        bill.customerMobile.includes(term.replace(/\D/g, ''))
    );
  }, [bills, search]);

  // The outstanding tile follows what is still owed, not what was sold - a bill
  // half paid should only weigh half as much on the total
  const outstanding = useMemo(
    () =>
      visibleBills.reduce(
        (sum, bill) => sum + (view === 'pending' ? bill.balance ?? 0 : bill.amount ?? 0),
        0
      ),
    [visibleBills, view]
  );

  const payableVisibleBills = useMemo(
    () =>
      view === 'pending'
        ? visibleBills.filter((bill) => (bill.balance ?? 0) > 0.005 && bill.dueStatus !== 'settled')
        : [],
    [visibleBills, view]
  );

  /**
   * One card per customer, not per bill.
   *
   * A regular on credit runs up several bills, and listing each as its own
   * section meant the same name and number repeated down the page with no way to
   * see what they owed in total - which is the only number worth chasing them
   * for. The bills stay separate inside the card because a repayment is recorded
   * against one bill, not against the person.
   *
   * Grouped on the number rather than the name: the name is whatever was typed
   * at the till that day. The last ten digits are used so a bill taken before
   * numbers were normalised - "+977 98..." against today's "98..." - still lands
   * on the same customer instead of splitting the slate in two.
   */
  const customerGroups = useMemo(() => {
    const groups = new Map();

    for (const bill of visibleBills) {
      const digits = (bill.customerMobile || '').replace(/\D/g, '');
      const key = digits
        ? digits.length > 10
          ? digits.slice(-10)
          : digits
        : `name:${(bill.customerName || '').toLowerCase()}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          customerName: bill.customerName,
          customerMobile: bill.customerMobile,
          bills: [],
          totalAmount: 0,
          totalBalance: 0,
          totalPaid: 0,
          latest: 0
        });
      }

      const group = groups.get(key);
      group.bills.push(bill);
      group.totalAmount += bill.amount ?? 0;
      group.totalBalance += bill.balance ?? 0;
      group.totalPaid += bill.paid ?? 0;

      // The most recent bill decides the name and number shown, so a corrected
      // spelling wins over whatever was typed months ago.
      const takenAt = bill.dueDate ? new Date(bill.dueDate).getTime() : 0;
      if (takenAt >= group.latest) {
        group.latest = takenAt;
        group.customerName = bill.customerName;
        group.customerMobile = bill.customerMobile;
      }
    }

    const list = Array.from(groups.values());

    for (const group of list) {
      group.bills.sort(
        (a, b) => new Date(b.dueDate || 0).getTime() - new Date(a.dueDate || 0).getTime()
      );
    }

    // Biggest debtor first when chasing money; most recent first when reviewing
    // what has already been collected.
    return list.sort((a, b) =>
      view === 'pending' ? b.totalBalance - a.totalBalance : b.latest - a.latest
    );
  }, [visibleBills, view]);

  const handlePay = async (bill, paymentType) => {
    const label = paymentType === 'cash' ? 'cash' : 'bank';
    const typed = (amounts[bill.dueRef] ?? '').trim();

    // An empty box means the whole balance, which is what most repayments are
    const amount = typed === '' ? bill.balance : parseFloat(typed);

    if (!Number.isFinite(amount) || amount <= 0) {
      showNotification('error', t('Enter an amount greater than zero.'));
      return;
    }

    if (amount > bill.balance + 0.005) {
      showNotification('error', t('Only Rs {amount} is outstanding on this due.', { amount: bill.balance.toFixed(2) }));
      return;
    }

    const isFull = amount >= bill.balance - 0.005;

    if (
      !(await askConfirm(
        `Record Rs ${amount.toFixed(2)} from ${bill.customerName} by ${label}?` +
          (isFull ? '' : ` Rs ${(bill.balance - amount).toFixed(2)} will stay outstanding.`)
      ))
    ) {
      return;
    }

    try {
      setSettling(bill.dueRef);
      const response = await api.post(`/api/orders/dues/${bill.dueRef}/payments`, {
        paymentType,
        amount
      });
      showNotification(
        'success',
        response.data.settled
          ? `${bill.customerName}'s due is paid off.`
          : `Rs ${amount.toFixed(2)} received. Rs ${response.data.balance.toFixed(2)} still owed.`
      );
      setAmounts((current) => ({ ...current, [bill.dueRef]: '' }));
      await loadDues();
      // The drawer moves when a repayment is taken
      window.dispatchEvent(new CustomEvent('revenueUpdate'));
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not record the payment.');
    } finally {
      setSettling(null);
    }
  };

  const handlePayAll = async (paymentType) => {
    const label = paymentType === 'cash' ? 'cash' : 'bank';
    const dueRefs = payableVisibleBills.map((bill) => bill.dueRef);
    const total = payableVisibleBills.reduce((sum, bill) => sum + (bill.balance ?? 0), 0);

    if (dueRefs.length === 0) {
      showNotification('error', 'No outstanding dues to complete.');
      return;
    }

    if (
      !(await askConfirm(
        `Complete ${dueRefs.length} visible due${dueRefs.length === 1 ? '' : 's'} by ${label}? ` +
          `This records Rs ${total.toFixed(2)} and pays every visible due in full.`
      ))
    ) {
      return;
    }

    try {
      setSettling('bulk');
      const response = await api.post('/api/orders/dues/bulk/payments', {
        paymentType,
        dueRefs
      });

      const failures = response.data.errors?.length || 0;
      showNotification(
        failures > 0 ? 'warning' : 'success',
        failures > 0
          ? `${response.data.settledCount} dues paid off. ${failures} could not be completed.`
          : `${response.data.settledCount} dues paid off by ${label}.`
      );
      setAmounts((current) => {
        const next = { ...current };
        for (const dueRef of dueRefs) delete next[dueRef];
        return next;
      });
      await loadDues();
      window.dispatchEvent(new CustomEvent('revenueUpdate'));
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not complete the dues.');
    } finally {
      setSettling(null);
    }
  };

  /**
   * One payment against a customer's whole slate. The customer hands over a
   * single figure - the cashier should not have to split it across bills, so
   * the server cascades it oldest-bill-first and the newest bills stay
   * outstanding if the money runs out. Blank means everything they owe.
   */
  const handlePayCustomer = async (group, paymentType) => {
    const label = paymentType === 'cash' ? 'cash' : 'bank';

    // Oldest first, so the cascade clears the longest-standing bills before
    // touching this week's.
    const payable = group.bills
      .filter((bill) => (bill.balance ?? 0) > 0.005 && bill.dueStatus !== 'settled')
      .slice()
      .sort((a, b) => new Date(a.dueDate || 0).getTime() - new Date(b.dueDate || 0).getTime());

    if (payable.length === 0) {
      showNotification('error', 'No outstanding dues for this customer.');
      return;
    }

    const totalBalance = payable.reduce((sum, bill) => sum + (bill.balance ?? 0), 0);
    const typed = (customerAmounts[group.key] ?? '').trim();
    const amount = typed === '' ? totalBalance : parseFloat(typed);

    if (!Number.isFinite(amount) || amount <= 0) {
      showNotification('error', t('Enter an amount greater than zero.'));
      return;
    }

    if (amount > totalBalance + 0.005) {
      showNotification(
        'error',
        `${group.customerName} only owes Rs ${totalBalance.toFixed(2)}.`
      );
      return;
    }

    const isFull = amount >= totalBalance - 0.005;

    if (
      !(await askConfirm(
        `Record Rs ${amount.toFixed(2)} from ${group.customerName} by ${label}?` +
          (isFull
            ? ' This clears everything they owe.'
            : ` Rs ${(totalBalance - amount).toFixed(2)} will stay outstanding.`)
      ))
    ) {
      return;
    }

    try {
      setSettling(`group:${group.key}`);
      const response = await api.post('/api/orders/dues/bulk/payments', {
        paymentType,
        dueRefs: payable.map((bill) => bill.dueRef),
        amount
      });

      const balanceLeft = totalBalance - (response.data.totalAmount ?? amount);
      showNotification(
        'success',
        balanceLeft <= 0.005
          ? `${group.customerName}'s dues are paid off.`
          : `Rs ${(response.data.totalAmount ?? amount).toFixed(2)} received. Rs ${balanceLeft.toFixed(2)} still owed.`
      );
      setCustomerAmounts((current) => ({ ...current, [group.key]: '' }));
      setAmounts((current) => {
        const next = { ...current };
        for (const bill of payable) delete next[bill.dueRef];
        return next;
      });
      await loadDues();
      window.dispatchEvent(new CustomEvent('revenueUpdate'));
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not record the payment.');
    } finally {
      setSettling(null);
    }
  };

  const handleReversePayment = async (bill, payment) => {
    if (
      !(await askConfirm(
        `Reverse the Rs ${payment.amount.toFixed(2)} payment taken by ${payment.paymentType}? The bill goes back to outstanding.`
      ))
    ) {
      return;
    }

    try {
      setSettling(bill.dueRef);
      await api.delete(`/api/orders/dues/${bill.dueRef}/payments/${payment._id}`);
      showNotification('success', t('Payment reversed.'));
      await loadDues();
      window.dispatchEvent(new CustomEvent('revenueUpdate'));
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not reverse the payment.');
    } finally {
      setSettling(null);
    }
  };

  const money = (value) => `Rs ${(value ?? 0).toFixed(2)}`;

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleString([], {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '';

  return (
    <div className="space-y-3 sm:space-y-6 max-w-3xl">
      {/* Pinned near the top on purpose: it opens right after typing an
          amount, and a centered dialog lands behind the phone keyboard. */}
      {confirmState && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 pt-[10vh]"
          onClick={() => closeConfirm(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm text-gray-900">{confirmState.message}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => closeConfirm(false)}
                className="flex-1 px-4 py-2.5 min-h-[44px] border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold"
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => closeConfirm(true)}
                className="flex-1 px-4 py-2.5 min-h-[44px] bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 transition-colors text-sm font-semibold"
              >
                {t('Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="hidden sm:block">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('Dues')}</h1>
        <p className="text-gray-600 mt-1">
          {t('Bills sold on credit, and who owes the money on each one.')}
        </p>
      </div>

      {/* Outstanding total - the one figure worth seeing first */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6">
        <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {view === 'pending' ? 'Outstanding' : 'Settled'}
        </p>
        <p className="text-3xl sm:text-4xl font-bold text-amber-600 leading-tight">
          {money(outstanding)}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {customerGroups.length} customer{customerGroups.length === 1 ? '' : 's'} ·{' '}
          {visibleBills.length} bill{visibleBills.length === 1 ? '' : 's'}
        </p>
        {view === 'pending' && payableVisibleBills.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handlePayAll('cash')}
              disabled={settling === 'bulk'}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-semibold"
            >
              <Banknote className="h-4 w-4" />
              {settling === 'bulk' ? '...' : 'Complete all cash'}
            </button>
            <button
              type="button"
              onClick={() => handlePayAll('bank')}
              disabled={settling === 'bulk'}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-semibold"
            >
              <Landmark className="h-4 w-4" />
              {settling === 'bulk' ? '...' : 'Complete all bank'}
            </button>
          </div>
        )}
      </div>

      {/* Pending / settled */}
      <div className="flex gap-2">
        {[
          { key: 'pending', label: 'Outstanding' },
          { key: 'settled', label: 'Settled' }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setView(tab.key)}
            className={`flex-1 px-4 py-2.5 min-h-[44px] rounded-xl text-sm font-semibold transition-colors ${
              view === tab.key
                ? 'bg-cafe-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('Search by name or mobile')}
          className="w-full pl-9 pr-3 py-2.5 min-h-[44px] border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cafe-500"
        />
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center text-sm text-gray-500">
          {t('Loading...')}
        </div>
      ) : customerGroups.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 text-center">
          <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-gray-900 mb-1">
            {view === 'pending' ? 'Nothing outstanding' : 'Nothing settled yet'}
          </h3>
          <p className="text-sm text-gray-500">
            {view === 'pending'
              ? 'Bills paid with Due at the till appear here.'
              : 'Dues you collect will be listed here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {customerGroups.map((group) => {
            const isOpen = openCustomers.has(group.key);
            const total = view === 'pending' ? group.totalBalance : group.totalAmount;

            return (
              <div
                key={group.key}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
              >
                {/*
                  Collapsed, this row is the whole point of the page: who owes
                  what. The bills behind it only matter once money is actually
                  being taken off one, so they stay out of the way until asked
                  for.
                */}
                <button
                  type="button"
                  onClick={() => toggleCustomer(group.key)}
                  aria-expanded={isOpen}
                  className="w-full text-left flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors"
                >
                  <ChevronDown
                    className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${
                      isOpen ? '' : '-rotate-90'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 truncate">{group.customerName}</h3>
                    <p className="text-xs text-gray-500 truncate">
                      {group.customerMobile} · {group.bills.length} bill
                      {group.bills.length === 1 ? '' : 's'}
                      {view === 'pending' && group.totalPaid > 0
                        ? ` · ${money(group.totalPaid)} paid`
                        : ''}
                    </p>
                  </div>
                  <span
                    className={`text-lg font-bold whitespace-nowrap flex-shrink-0 ${
                      view === 'pending' ? 'text-amber-600' : 'text-gray-400'
                    }`}
                  >
                    {money(total)}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100">
                    {/* Outside the toggle button: a nested link would be invalid
                        markup and would fire the collapse on the way to dialling. */}
                    <div className="px-3.5 py-2 bg-gray-50 border-b border-gray-100">
                      <a
                        href={`tel:${group.customerMobile}`}
                        className="text-sm text-cafe-700 font-medium inline-flex items-center gap-1.5"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        Call {group.customerMobile}
                      </a>
                    </div>

                    {/* One payment for the whole slate. The customer hands over
                        a single figure - it cascades oldest-bill-first, so the
                        cashier never has to split it across bills below. */}
                    {view === 'pending' && group.totalBalance > 0.005 && (
                      <div className="p-3.5 bg-amber-50/60 border-b border-gray-100 space-y-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                          Pay towards all {group.bills.length} bill
                          {group.bills.length === 1 ? '' : 's'}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="number"
                            min="0"
                            max={group.totalBalance}
                            step="0.01"
                            value={customerAmounts[group.key] ?? ''}
                            onChange={(e) =>
                              setCustomerAmounts((current) => ({
                                ...current,
                                [group.key]: e.target.value
                              }))
                            }
                            placeholder={`Full · ${group.totalBalance.toFixed(2)}`}
                            aria-label={t('Amount to pay')}
                            className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-cafe-500"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handlePayCustomer(group, 'cash')}
                              disabled={settling === `group:${group.key}` || settling === 'bulk'}
                              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-semibold"
                            >
                              <Banknote className="h-4 w-4" />
                              {settling === `group:${group.key}` ? '...' : 'Cash'}
                            </button>
                            <button
                              onClick={() => handlePayCustomer(group, 'bank')}
                              disabled={settling === `group:${group.key}` || settling === 'bulk'}
                              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-semibold"
                            >
                              <Landmark className="h-4 w-4" />
                              {settling === `group:${group.key}` ? '...' : 'Bank'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="divide-y divide-gray-100">
                      {group.bills.map((bill) => {
                        const isSettled = bill.dueStatus === 'settled';
                        const busy =
                          settling === bill.dueRef ||
                          settling === 'bulk' ||
                          settling === `group:${group.key}`;
                        const partPaid = bill.paid > 0;

                        return (
                          <div key={bill.dueRef} className="p-3.5 space-y-2.5">
                            <div className="flex items-baseline justify-between gap-3">
                              <p className="text-sm font-medium text-gray-900 min-w-0 truncate">
                                {formatDate(bill.dueDate)}
                                {bill.tokenNumber ? ` · #${bill.tokenNumber}` : ''}
                              </p>
                              <div className="text-right flex-shrink-0">
                                <span
                                  className={`text-base font-bold whitespace-nowrap ${
                                    isSettled ? 'text-gray-400' : 'text-amber-600'
                                  }`}
                                >
                                  {money(isSettled ? bill.amount : bill.balance)}
                                </span>
                                {/* Only worth spelling out once part is paid */}
                                {!isSettled && partPaid && (
                                  <span className="block text-xs text-gray-500 whitespace-nowrap">
                                    of {money(bill.amount)}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="text-sm text-gray-600 space-y-0.5">
                              {bill.items.map((line, index) => (
                                <div
                                  key={`${bill.dueRef}-${index}`}
                                  className="flex justify-between gap-4"
                                >
                                  <span className="truncate">
                                    {orderLabel(line, language)}
                                    {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                                  </span>
                                  <span className="flex-shrink-0 tabular-nums">
                                    {money(line.amount)}
                                  </span>
                                </div>
                              ))}
                            </div>

                            {/* Every instalment taken, so a customer paying in
                                dribs can be shown exactly what they handed over
                                and when. */}
                            {bill.payments.length > 0 && (
                              <div className="rounded-lg bg-green-50 border border-green-100 p-2 space-y-1">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-green-800">
                                  Payments received · {money(bill.paid)}
                                </p>
                                {bill.payments.map((payment) => (
                                  <div
                                    key={payment._id}
                                    className="flex items-center justify-between gap-2 text-sm"
                                  >
                                    <span className="text-green-800 inline-flex items-center gap-1.5 min-w-0">
                                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                                      <span className="truncate">
                                        {money(payment.amount)} · {payment.paymentType} ·{' '}
                                        {formatDate(payment.paidAt)}
                                      </span>
                                    </span>
                                    <button
                                      onClick={() => handleReversePayment(bill, payment)}
                                      disabled={busy}
                                      className="text-red-600 hover:text-red-700 disabled:opacity-50 flex-shrink-0 p-1"
                                      title={t('Reverse this payment')}
                                      aria-label={`Reverse the ${money(payment.amount)} payment`}
                                    >
                                      <Undo2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {isSettled ? (
                              <p className="text-sm text-green-700 font-medium inline-flex items-center gap-1.5">
                                <CheckCircle2 className="h-4 w-4" />
                                Paid off {formatDate(bill.settledAt)}
                              </p>
                            ) : (
                              <div className="flex flex-col sm:flex-row gap-2">
                                {/* Blank takes the whole balance - part payment
                                    is the exception, so it should not cost a
                                    keystroke to skip */}
                                <input
                                  type="number"
                                  min="0"
                                  max={bill.balance}
                                  step="0.01"
                                  value={amounts[bill.dueRef] ?? ''}
                                  onChange={(e) =>
                                    setAmounts((current) => ({
                                      ...current,
                                      [bill.dueRef]: e.target.value
                                    }))
                                  }
                                  placeholder={`Full · ${bill.balance.toFixed(2)}`}
                                  aria-label={t('Amount to pay')}
                                  className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handlePay(bill, 'cash')}
                                    disabled={busy}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-semibold"
                                  >
                                    <Banknote className="h-4 w-4" />
                                    {busy ? '...' : 'Cash'}
                                  </button>
                                  <button
                                    onClick={() => handlePay(bill, 'bank')}
                                    disabled={busy}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-semibold"
                                  >
                                    <Landmark className="h-4 w-4" />
                                    {busy ? '...' : 'Bank'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Dues;
